import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Spinner, Alert, Table, Modal, Form, InputGroup, FormControl } from 'react-bootstrap';
import axios from 'axios';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import '../AdminDashboard.css';

const MasterFeatureListManagement = ({ app_url, embedded = false, onClose = null }) => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    is_active: true
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const user_role = Cookies.get('user_role');

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${app_url}/api/subscriptions/master-features`, {
        withCredentials: true
      });
      if (response.data.success) {
        setFeatures(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching master features:', error);
      toast.error('Failed to load master features');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFeature = () => {
    setEditingFeature(null);
    setFormData({ name: '', is_active: true });
    setShowCreateModal(true);
  };

  const handleEditFeature = (feature) => {
    setEditingFeature(feature);
    setFormData({
      name: feature.name,
      is_active: feature.is_active
    });
    setShowCreateModal(true);
  };

  const handleSaveFeature = async () => {
    if (!formData.name || !formData.name.trim()) {
      toast.error('Please enter a feature name');
      return;
    }

    setSaving(true);
    try {
      if (editingFeature) {
        const response = await axios.put(
          `${app_url}/api/subscriptions/master-features/${editingFeature._id}`,
          {
            name: formData.name.trim(),
            is_active: formData.is_active
          },
          { withCredentials: true }
        );

        if (response.data.success) {
          toast.success('Feature updated successfully');
          setShowCreateModal(false);
          fetchFeatures();
        }
      } else {
        const response = await axios.post(
          `${app_url}/api/subscriptions/master-features`,
          { name: formData.name.trim() },
          { withCredentials: true }
        );

        if (response.data.success) {
          toast.success('Feature created successfully');
          setShowCreateModal(false);
          fetchFeatures();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save feature');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFeature = async (feature) => {
    if (!window.confirm(`Are you sure you want to ${feature.is_active ? 'archive' : 'delete'} "${feature.name}"?`)) return;

    try {
      if (feature.is_active) {
        await axios.put(
          `${app_url}/api/subscriptions/master-features/${feature._id}`,
          { name: feature.name, is_active: false },
          { withCredentials: true }
        );
        toast.success('Feature archived');
      } else {
        await axios.delete(`${app_url}/api/subscriptions/master-features/${feature._id}`, {
          withCredentials: true
        });
        toast.success('Feature deleted');
      }
      fetchFeatures();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const filteredFeatures = features
    .filter(f => f.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter(f => filterStatus === '' || (filterStatus === 'active' ? f.is_active : !f.is_active));

  if (user_role !== 'admin') {
    return <Alert variant="danger">Admin privileges required.</Alert>;
  }

  if (loading) {
    return <Spinner />;
  }

  return (
    <>
      <div className={`${embedded ? '' : 'main-container '}master-feature-list-container`}>

        <Container fluid>
          <div className="admin-section">

            {/* Header */}
            <div className="d-flex justify-content-between mb-3">
              <h2>Master Features</h2>
              <Button onClick={handleCreateFeature} className="sxi_primary_blue_btn btn-height">
                Create Feature
              </Button>
            </div>

            {/* Search */}
            <Row className="mb-3">
              <Col md={6}>
                <InputGroup>
                  <FormControl
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={6}>
                <FormControl
                  as="select"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </FormControl>
              </Col>
            </Row>

            {/* Table */}
            <div className="table-responsive">
              <Table style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '45%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '25%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Status</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Created</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFeatures.map((f) => (
                    <tr key={f._id}>
                      <td
                        title={f.name}
                        style={{
                          maxWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {f.name}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{f.is_active ? 'Active' : 'Inactive'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{new Date(f.createdAt).toLocaleDateString()}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <Button size="sm" className="btn-height me-2" variant="outline-primary" onClick={() => handleEditFeature(f)}>
                          Edit
                        </Button>
                        <Button size="sm" className="btn-height" variant="danger" onClick={() => handleDeleteFeature(f)}>
                          {f.is_active ? 'Archive' : 'Delete'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

          </div>
        </Container>
      </div>

      {/* ✅ Modal moved OUTSIDE main div (fixes error) */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingFeature ? 'Edit' : 'Create'} Feature</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Control
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Feature name"
              maxLength={80}
            />
            <div className="text-muted small mt-1 text-end">
              {formData.name.length}/80
            </div>
            {editingFeature && (
              <Form.Check
                type="checkbox"
                label="Active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button className="sxi_primary_blue_btn btn-height" onClick={handleSaveFeature} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button variant="secondary" className="btn-height" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MasterFeatureListManagement;